/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/anchor-is-valid */
/**
PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 *  Copyright © 2021 Pearson Education, Inc.
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
* */

/**
 * QRCodeModal component
 *
 * @file modal.jsx
 * @author Ujjwal Sana
 * @since 0.0.1
 */

import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Dialog, CircularProgress } from '@mui/material';
import { Icon } from '@greenville/mui-theme';
import QRCode from 'react-qr-code';
import {
  DialogPaper,
  DialogContentContainer,
  InfoDescriptionWrapper,
  DialogQRSubheader,
  QrImageContainer,
  QrErrorContainer,
  LoaderContainer,
  DialogErrorMsg,
  CloseContainer,
  CloseButtonText,
  QrMsgContainer,
  DialogQueMsg,
  DialogFooterMsg,
  DialogFooterAction,
  DialogTitleInfoContanier,
  DialogHeader,
  DialogAction,
  CloseIconButton
} from './styles/QRCodeStyles';
import ThemeVariablesRoot from '../theme/ThemeVariablesRoot';
import { THEMES } from '../../constants';
import QRCodeErrorIcon from '../../icons/QRCodeErrorIcon';

const DIALOG_LABEL_ID = 'dialog_label';
const DIALOG_DESC_ID = 'dialog_desc';
const DIALOG_MSG_ID = 'dialog_msg';
const DIALOG_FOOTER_ID = 'dialog_footer';
const DIALOG_ERROR_ID = 'dialog_error';

const QRCodeModal = ({
  data,
  callbacks
}) => {
  const {
    open,
    header,
    subHeader,
    ignoreAppQrCode,
    footer,
    qrCode,
    isQRCodeMsg,
    close
  } = data;
  const { handleQrCodeClick } = callbacks;

  const qrCodeModalRef = useRef(null);

  const ariaDescribedBy = [
    DIALOG_DESC_ID,
    DIALOG_MSG_ID,
    DIALOG_FOOTER_ID,
    isQRCodeMsg ? DIALOG_ERROR_ID : null
  ].filter(Boolean).join(' ');

  const renderQrContainer = () => {
    if (qrCode) {
      return (
        <QrImageContainer>
          <QRCode value={qrCode} size={200} role="img" aria-label={subHeader} />
        </QrImageContainer>
      );
    }
    return (
      <QrErrorContainer aria-busy={!isQRCodeMsg && !qrCode}>
        { !isQRCodeMsg && !qrCode && (
          <LoaderContainer role="status">
            <CircularProgress aria-label={subHeader} />
          </LoaderContainer>
        )}
        {isQRCodeMsg && <QRCodeErrorIcon />}
        {isQRCodeMsg && (
          <DialogErrorMsg id={DIALOG_ERROR_ID} role="alert">
            {isQRCodeMsg}
          </DialogErrorMsg>
        )}
        {isQRCodeMsg && (
        <CloseContainer
          id="close-button"
          aria-label="Close"
          onClick={() => handleQrCodeClick()}
        >
          <CloseButtonText>{close}</CloseButtonText>
        </CloseContainer>
        )}
      </QrErrorContainer>
    );
  };

  return (
    <>
      <ThemeVariablesRoot theme={THEMES.LIGHT}>
        <Dialog
          open={open}
          id="title-info-dialog"
          onClose={handleQrCodeClick}
          aria-labelledby={DIALOG_LABEL_ID}
          aria-describedby={ariaDescribedBy}
          role="dialog"
          disablePortal
          maxWidth={false}
          disableRestoreFocus
          slotProps={{
            paper: {
              component: DialogPaper,
              tabIndex: 0,
              ref: qrCodeModalRef,
              'aria-modal': 'true'
            },
            transition: {
              onEntered: () => {
                if (qrCodeModalRef && qrCodeModalRef.current) {
                  qrCodeModalRef.current.focus();
                }
              }
            }
          }}
        >
          <DialogContentContainer
            id="title-info-dialog-description"
          >
            <InfoDescriptionWrapper>
              <DialogQRSubheader component="p" id={DIALOG_DESC_ID}>
                {subHeader}
              </DialogQRSubheader>
              {renderQrContainer()}
              <QrMsgContainer>
                <DialogQueMsg component="p" id={DIALOG_MSG_ID}>
                  {ignoreAppQrCode}
                </DialogQueMsg>
                <DialogFooterMsg component="p" id={DIALOG_FOOTER_ID}>
                  {footer}
                </DialogFooterMsg>
              </QrMsgContainer>
              <DialogFooterAction>
                <a aria-label="Download on Appstore" href="https://apps.apple.com/us/app/pearson-us-etexts/id1571362338" target="_blank" rel="noopener noreferrer">
                  <img alt="Appstore" src="https://plus.pearson.com/auth-home/media/images/message-center/download_appstore.png" />
                </a>
                <a aria-label="Get it in Playstore" href="https://play.google.com/store/apps/details?id=com.pearson.plus" target="_blank" rel="noopener noreferrer">
                  <img alt="Playstore" src="https://plus.pearson.com/auth-home/media/images/message-center/download_playstore.png" />
                </a>
              </DialogFooterAction>
            </InfoDescriptionWrapper>
          </DialogContentContainer>
          <DialogTitleInfoContanier>
            <DialogHeader variant="h3" component="h2" id={DIALOG_LABEL_ID}>
              {header}
            </DialogHeader>
            <DialogAction>
              <CloseIconButton
                id="remove-button"
                aria-label="Close"
                onClick={() => handleQrCodeClick()}
              >
                <Icon id="remove-icon" name="Remove" color="action" fontSize="large" />
              </CloseIconButton>
            </DialogAction>
          </DialogTitleInfoContanier>
        </Dialog>
      </ThemeVariablesRoot>
    </>
  );
};

QRCodeModal.propTypes = {
  data: PropTypes.shape({
    open: PropTypes.bool,
    header: PropTypes.string,
    subHeader: PropTypes.string,
    ignoreAppQrCode: PropTypes.string,
    qrCode: PropTypes.string,
    footer: PropTypes.string,
    isQRCodeMsg: PropTypes.string,
    close: PropTypes.string
  }).isRequired,
  callbacks: PropTypes.shape({
    handleQrCodeClick: PropTypes.func
  }).isRequired
};

export default QRCodeModal;
